import React, { useEffect, useState } from "react";
import { useStore } from "../store";
import { useNavigate, useParams } from "react-router-dom";
import { Grid, Masthead, Button, ButtonGroup, Box, Snackbar, IconButton, Typography } from '@barracuda-internal/bds-core';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';

import Summary from "./Summary";
import SummaryLoading from "./SummaryLoading";
import Vulnerabilities from "../scanresult/Vulnerabilities";
import ScoreCalculation from "../scanresult/ScoreCalculation";
import SubDomains from "../scanresult/SubDomains";
import VulnerabilitiesLoading from "../scanresult/VulnerabilitiesLoading";
import SubDomainsLoading from "../scanresult/SubDomainsLoading";
import Secure_config from "../scanresult/SecureConfiguration";
import axios from "axios";
import { API_BASE_URL } from "../settings";
import api from "../api";

export default function ScanResultAdvanced() {
  const { state } = useStore();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanStatus, setScanStatus] = useState('in_progress');
       const [complianceFilter, setComplianceFilter] = useState(null);
  // Refs for each section and scrollable area
  const summaryRef = React.useRef(null);
  const vulnerabilitiesRef = React.useRef(null);
  const secureConfigRef = React.useRef(null);
  const scoreCalcRef = React.useRef(null);
  const scrollAreaRef = React.useRef(null);
  const subDomainRef = React.useRef(null);

  useEffect(() => {
    let pollingInterval = null;
    let isUnmounted = false;
    async function fetchScanResult() {
      setLoading(true);
      try {
        const resp = await api.get(`${API_BASE_URL}/api/public/scan-result/${uuid}/`, { withCredentials: true });
        setScanResult(resp.data);
        // Set scan status after receiving response
        const status = resp.data?.scan?.status;
        if (["completed", "failed", "cancelled"].includes(status)) {
          setScanStatus(status);
        } else {
          setScanStatus("in_progress");
        }
        // If status is in_progress, start polling
        if (status === "in_progress" && !pollingInterval) {
          pollingInterval = setInterval(async () => {
            if (isUnmounted) return;
            try {
              const pollResp = await api.get(`${API_BASE_URL}/api/public/scan-result/${uuid}/`, { withCredentials: true });
              setScanResult(pollResp.data);
              const pollStatus = pollResp.data?.scan?.status;
              if (["completed", "failed", "cancelled"].includes(pollStatus)) {
                setScanStatus(pollStatus);
                clearInterval(pollingInterval);
                pollingInterval = null;
              }
            } catch (e) {
              // Optionally handle polling error
            }
          }, 10000); // 10 seconds
        }
        // If status is not in_progress, clear polling
        if (["completed", "failed", "cancelled"].includes(status) && pollingInterval) {
          clearInterval(pollingInterval);
          pollingInterval = null;
        }
      } catch (e) {
        setScanResult(null);
        setScanStatus("failed");
        console.error("Error fetching scan result:", e);
      } finally {
        setLoading(false);
      }
    }
    if (uuid) fetchScanResult();
    return () => {
      isUnmounted = true;
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [uuid]);

  const handleSectionClick = (newValue, complianceType = null, idList = null) => {

  // User clicked a compliance button in Summary
  if (newValue === 1 && complianceType && idList) {
    setComplianceFilter({ type: complianceType, ids: idList });
  }

  // User clicked "Vulnerabilities" tab manually
  else if (newValue === 1 && !complianceType) {
    setComplianceFilter(null);  // CLEAR FILTER
  }

  setTabIndex(newValue);

  let ref = null;
  if (newValue === 0) ref = summaryRef;
  else if (newValue === 1) ref = vulnerabilitiesRef;
  else if (newValue === 2) ref = secureConfigRef;
  else if (newValue === 3) ref = subDomainRef;
  else if (newValue === 4) ref = scoreCalcRef;

  if (ref?.current) {
    const scrollContainer = ref.current.parentElement;
    const stickyOffset = 64 + 80;
    const sectionTop = ref.current.offsetTop;

    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: sectionTop - stickyOffset,
        behavior: "smooth",
      });
    }
  }
};
  const handleShare = () => {
    if (!uuid) return;
    // Construct the public shareable URL
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/public/scan-result/${uuid}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
    } else {
      // fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setSnackbarOpen(true);
  };
// useEffect(() => {
//   const handleClick = (e) => {

//     // If the user clicked a GDPR button, do NOT reset
//     if (e.target.closest(".no-reset")) return;

//     setComplianceFilter(null);
//   };

//   document.addEventListener("click", handleClick);

//   return () => document.removeEventListener("click", handleClick);
// }, []);
  // Update tabIndex on scroll
  useEffect(() => {
    const scrollContainer = scrollAreaRef.current;
    if (!scrollContainer) return;
    const handleScroll = () => {
      // Height of sticky masthead + button group
      const stickyOffset = 64 + 100;
      const scrollTop = scrollContainer.scrollTop;
      // Get top positions relative to scroll area
      const sectionTops = [
        summaryRef.current?.offsetTop ?? 0,
        vulnerabilitiesRef.current?.offsetTop ?? 0,
        secureConfigRef.current?.offsetTop ?? 0,
        subDomainRef.current?.offsetTop ?? 0,
        scoreCalcRef.current?.offsetTop ?? 0,
      ];
      // Find the section whose top is closest but not greater than scrollTop + stickyOffset
      let active = 0;
      for (let i = 0; i < sectionTops.length; i++) {
        if (scrollTop + stickyOffset >= sectionTops[i]) {
          active = i;
        }
      }
      setTabIndex(active);
    };
    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // Sticky header and tabs, scrollable content area
  return (
    <Grid style={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={() => setSnackbarOpen(false)}
          message="Sharable link copied to clipboard!"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      {/* Sticky masthead */}
      <Grid style={{ position: 'sticky', top: 0, zIndex: 100 }}>
        <Masthead
          pageTitle={"Security Insights" + (scanResult?.scan?.domain ? ` - ${scanResult.scan.domain}` : "")}
          productTitle="WAF-as-a-Service"
          belowContent={null}
          rightContent={
            <>
              <Button
                bdsType="interactiveNeutral"
                data-action="free-trial"
                onClick={() => navigate("/scan")}
              >
                Start a new Analysis
              </Button>
              <Button bdsType="interactiveEmphasis" data-action="try" onClick={() => window.open('https://www.barracuda.com/products/application-protection/try-free', '_blank', 'noopener,noreferrer')}>Try WAF-as-a-Service now</Button>
              {state.isAuthenticated && (
                <IconButton bdsSize="input" data-action="share" onClick={handleShare}><ShareOutlinedIcon /></IconButton>
              )}
            </>
          }
        />
        <Grid container spacing={5} alignItems="center" sx={{ pl: 3, pb: 1 }}>
          <Grid item>
           {/* <Typography sx={{ paddingLeft: 3 }} variant="subtitle2" color="text.secondary">Security Score: <b>{scanResult?.findings?.summary?.security_posture_score  ? scanResult?.findings?.summary?.security_posture_score : "-"} / 100 </b></Typography>  */}
          <Typography sx={{ paddingLeft: 3 }} variant="subtitle2" color="text.secondary"> Completed At: <b>{scanResult?.scan?.completed_at ? new Date(scanResult?.scan?.completed_at).toLocaleString() : "-"}</b></Typography> </Grid>
          <Grid item> <Typography sx={{ paddingLeft: 3 }} variant="subtitle2" color="text.secondary"> Issues: <b>{scanResult?.findings?.summary?.vulnerabilities_detected  ? scanResult?.findings?.summary?.vulnerabilities_detected : "-"}</b></Typography> </Grid>
          <Grid item> <Typography sx={{ paddingLeft: 3 }} variant="subtitle2" color="text.secondary"> WAF: <b>{scanResult?.findings?.waf_detected?.detected !== true ? "No WAF detected" : scanResult?.findings?.waf_detected?.manufacturer}</b></Typography> </Grid>
          {/* <Grid item> <Typography sx={{ py: 1, paddingLeft: 3 }} variant="subtitle2" color="text.secondary">✓ Completed At: {scanResult?.scan?.completed_at ? new Date(scanResult?.scan?.completed_at).toLocaleString() : "-"}</Typography> </Grid> */}
        </Grid>
        {/* Sticky tabs */}
        <Box display="flex" justifyContent="center" alignItems="center" style={{ position: 'sticky', top: 64, zIndex: 99 }}>
          <ButtonGroup bdsType="interactiveSubtle" aria-label="outlined primary button group">
            <Button selected={tabIndex === 0} onClick={() => handleSectionClick(0)}>Summary</Button>
            <Button selected={tabIndex === 1} onClick={() => handleSectionClick(1)}>Vulnerabilities</Button>
            <Button selected={tabIndex === 2} onClick={() => handleSectionClick(2)}>Secure Configuration</Button>
            <Button selected={tabIndex === 3} onClick={() => handleSectionClick(3)}>Subdomains</Button>
            {/* <Button selected={tabIndex === 4} onClick={() => handleSectionClick(4)}>Score Calculation</Button> */}
          </ButtonGroup>
        </Box>
      </Grid>
      {/* Scrollable content area for sections */}
      <Grid ref={scrollAreaRef} style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        <Grid ref={summaryRef}>
          {
            (loading || scanStatus === 'in_progress') ?
              <SummaryLoading target_domain={scanResult?.scan?.domain || ""} scrollPage={handleSectionClick} /> :
              <Summary scrollPage={handleSectionClick} state={state} ai_summary={scanResult?.ai_summary || null} summary={scanResult?.findings?.summary || {}} framework_summary={scanResult?.findings?.framework_info?.report_summary || {}} isLoading={loading} vulnerabilities={scanResult?.findings?.vulnerabilities || []} waf_detected={scanResult?.findings?.waf_detected || {}} sub_domains_count={scanResult?.findings?.sub_domains?.length || 0} />
          }
        </Grid>
        <Grid ref={vulnerabilitiesRef}>
          {
            (loading || scanStatus === 'in_progress') ?
              <VulnerabilitiesLoading title={"Possible Attacks"} subtitle={"You are Vulnerable to following Attacks"} /> :
             <Vulnerabilities
  isSafe={false}
  title={"Possible Attacks"}
  subtitle={"You are Vulnerable to following Attacks"}
  state={state}
  url={scanResult?.scan?.domain || ""}
  isLoading={loading}
  vulnerabilities={scanResult?.findings?.vulnerabilities || []}
  complianceFilter={complianceFilter}
  setComplianceFilter={setComplianceFilter} 
/>
          }
        </Grid>
        <Grid ref={secureConfigRef}>
          {
            (loading || scanStatus === 'in_progress') ?
              <VulnerabilitiesLoading title={"Secure Configuration"} subtitle={"component passed security checks"} /> :
              <Secure_config isSafe={true} title={"Secure Configuration"} subtitle={"component passed security checks"} state={state} url={scanResult?.scan?.domain || ""} isLoading={loading} vulnerabilities={scanResult?.findings?.secure_config || []} />
          }

        </Grid>
        <Grid ref={subDomainRef}>
          {
            (loading || scanStatus === 'in_progress') ?
              <SubDomainsLoading /> :
              <SubDomains state={state} isLoading={loading} scanResult={scanResult} subdomains={scanResult?.findings?.sub_domains || []} />
          }
        </Grid>
        <Grid ref={scoreCalcRef}>
          {/* <ScoreCalculation state={state} scanResult={scanResult} /> */}
        </Grid>
      </Grid>
    </Grid>
  );
}
